import { createSlice } from "@reduxjs/toolkit";
import { defaultColumnHeaders } from "../../helpers";

const getOrCreateTargetView = (state, viewCategory) => {
  const viewType = viewCategory === 1 ? "bid" : "daily";
  const defaultColumns = state.defaultColumns[viewType];
  let targetView = state.checkedColumn[viewCategory];

  if (!targetView) {
    targetView = state.customViews[viewType]?.find((view) => view.isBookMark);
  }

  if (!targetView) {
    targetView = {
      viewName: "User Custom View",
      viewCategory,
      isBookMark: false,
      tableDetails: defaultColumns.map((column) => ({
        columnName: column.columnName,
        priority: column.columnNumber,
      })),
    };
    state.customViews[viewType] = [
      ...(state.customViews[viewType] || []),
      targetView,
    ];
  } else if (!state.checkedColumn[viewCategory]) {
    const viewIndex = state.customViews[viewType].indexOf(targetView);

    if (viewIndex !== -1) {
      targetView = {
        ...targetView,
        tableDetails: [...targetView.tableDetails],
      };
      state.customViews[viewType][viewIndex] = targetView;
    }
  }

  state.checkedColumn[viewCategory] = targetView;
  return targetView;
};

const drmCustomColumnSlice = createSlice({
  name: "customColumn",
  initialState: {
    defaultColumns: {
      bid: defaultColumnHeaders.bid.columns,
      daily: defaultColumnHeaders.daily.columns
    },
    customViews: {
      bid: [], 
      daily: [] 
    },
    isLoading: false,
    checkedColumn: {
      1: null,
      2: null
    },
    isChecked: false,
    errorMessage: undefined,
  },
  reducers: {
    getColumView: (state, action) => {
      state.isLoading = true;
    },
    setCheckedCustomColumn: (state, action) => {
      const { view, viewCategory } = action.payload;
      if (viewCategory === 1) {
        state.checkedColumn[1] = view;
      } else if (viewCategory === 2) {
        state.checkedColumn[2] = view;
      }
    },
    setCustomColumnViewSuccess: (state, action) => {
      state.isLoading = false;
      const { views, viewCategory } = action.payload;
      
      if (viewCategory === '1') {
        state.customViews.bid = views || [];
        if (!state.checkedColumn[1]) {
          const bookmarkedView = views?.find(v => v.isBookMark);
          state.checkedColumn[1] = bookmarkedView || null;
        }
      } else if (viewCategory === '2') {
        state.customViews.daily = views || [];
        if (!state.checkedColumn[2]) {
          const bookmarkedView = views?.find(v => v.isBookMark);
          state.checkedColumn[2] = bookmarkedView || null;
        }
      }
      state.errorMessage = undefined;
    },
    setCustomColumnViewFailure: (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.payload;
    },
    reSetCustomColumnView: (state, action) => {
      const viewCategory = action.payload?.viewCategory;
      if (viewCategory === 1) {
        state.customViews.bid = [];
         state.checkedColumn[1] = null;
      } else if (viewCategory === 2) {
        state.customViews.daily = [];
        state.checkedColumn[2] = null;
      } else {
        state.customViews.bid = [];
        state.customViews.daily = [];
        state.checkedColumn[1] = null;
        state.checkedColumn[2] = null;
      }
      state.isLoading = false;
      state.errorMessage = undefined;
    },
    updateColumnVisibility: (state, action) => {
      const { viewCategory, columnName, isVisible } = action.payload;
      const targetView = getOrCreateTargetView(state, viewCategory);

      if (isVisible) {
        if (!targetView.tableDetails.find(col => col.columnName === columnName)) {
          const maxPriority = Math.max(...targetView.tableDetails.map(col => col.priority), 0);
          targetView.tableDetails.push({
            columnName,
            priority: maxPriority + 1
          });
          targetView.tableDetails.sort((a, b) => a.priority - b.priority);
        }
      } else {
        targetView.tableDetails = targetView.tableDetails.filter(
          col => col.columnName !== columnName
        );
      }
    },
    reorderColumn: (state, action) => {
      const { viewCategory, columnName, direction } = action.payload;
      const targetView = getOrCreateTargetView(state, viewCategory);

      const currentIndex = targetView.tableDetails.findIndex(col => col.columnName === columnName);
      if (currentIndex !== -1) {
        if (direction === 'top' && currentIndex > 0) {
          const [movedColumn] = targetView.tableDetails.splice(currentIndex, 1);
          targetView.tableDetails.unshift(movedColumn);
          targetView.tableDetails.forEach((col, idx) => {
            col.priority = idx + 1;
          });
        } else if ((direction === 'bottom') && currentIndex < targetView.tableDetails.length - 1) {
          const [movedColumn] = targetView.tableDetails.splice(currentIndex, 1);
          targetView.tableDetails.push(movedColumn);
          targetView.tableDetails.forEach((col, idx) => {
            col.priority = idx + 1;
          });
        } else {
          const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
          if (newIndex >= 0 && newIndex < targetView.tableDetails.length) {
            const temp = targetView.tableDetails[currentIndex].priority;
            targetView.tableDetails[currentIndex].priority = targetView.tableDetails[newIndex].priority;
            targetView.tableDetails[newIndex].priority = temp;
            targetView.tableDetails.sort((a, b) => a.priority - b.priority);
          }
        }
      }
    },
    updateColumnBookmark: (state, action) => {
      const { viewCategory, viewCode, isBookMark, applyOnly, tableDetails } = action.payload;
      const viewType = viewCategory === 1 ? 'bid' : 'daily';
      
      if (applyOnly && tableDetails) {
        if (viewCode) {
          state.customViews[viewType] = state.customViews[viewType].map(view => {
            if (view.viewCode === viewCode) {
              return { ...view, isBookMark: true };
            }
            return { ...view, isBookMark: false };
          });
        } else {
          state.customViews[viewType] = state.customViews[viewType].map(view => ({
            ...view,
            isBookMark: false
          }));
        }
      } else if (!applyOnly) {
        state.customViews[viewType] = state.customViews[viewType].map(view => {
          if (view.viewCode === viewCode) {
            return { ...view, isBookMark: isBookMark };
          }
          return { ...view, isBookMark: isBookMark ? false : view.isBookMark };
        });
    
        state.isLoading = true;
      }
    },
    saveColumnBook: (state) => {
      state.isLoading = true;
    },
    saveColumnBookmarkSuccess: (state, action) => {
      state.isLoading = false;
    },
    saveColumnBookmarkFailure: (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.payload;
    },
    deleteCustomColumn: (state) => {
      state.isLoading = true;
    },
    deleteCustomColumnSuccess: (state, action) => {
      state.isLoading = false;
    },
    deleteCustomColumnFailure: (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.payload;
    }
  },
});

export const {
  getColumView,
  setCheckedCustomColumn,
  setCustomColumnViewSuccess,
  setCustomColumnViewFailure,
  reSetCustomColumnView,
  updateColumnVisibility,
  reorderColumn,
  updateColumnBookmark,
  saveColumnBook,
  saveColumnBookmarkSuccess,
  saveColumnBookmarkFailure,
  deleteCustomColumn,
  deleteCustomColumnSuccess,
  deleteCustomColumnFailure
} = drmCustomColumnSlice.actions;

export default drmCustomColumnSlice.reducer;